/**
 * DlerCard Component
 * Direct Labour Efficiency Ratio = Gross Margin $ / Direct Labour $
 * Benchmark: ≥3.0 for professional services
 */

import { StatusBadge } from './StatusBadge';
import { CalcTooltip } from './CalcTooltip';
import { CommentButton } from './CommentButton';
import { formatCurrency } from '@/utils/formatters';
import type { DlerMetric } from '@/types/scalingUp';

interface DlerCardProps {
  data: DlerMetric | null;
  loading: boolean;
  onInsight?: () => void;
}

export function DlerCard({ data, loading, onInsight }: DlerCardProps) {
  if (loading && !data) {
    return (
      <div className="scaling-up__card">
        <h2 className="scaling-up__card-title">DLER</h2>
        <div className="skeleton" style={{ height: '100px' }} />
      </div>
    );
  }

  if (!data) return null;

  const noData = data.directLabourDollars === 0;

  return (
    <div className="scaling-up__card">
      <div className="scaling-up__card-header">
        <h2 className="scaling-up__card-title">
          DLER
          <CalcTooltip title="Direct Labour Efficiency Ratio">
            <p><strong>Formula:</strong> <code>GM$ ÷ DL$</code></p>
            <p><strong>Gross Margin</strong>: {formatCurrency(data.grossMarginDollars)}</p>
            <p><strong>Direct Labour</strong>: {formatCurrency(data.directLabourDollars)} (wages, salaries, super from P&amp;L)</p>
            <p>For every $1 on team → ${data.ratio.toFixed(2)} gross margin. Target: ≥{data.benchmark}.</p>
          </CalcTooltip>
        </h2>
        <StatusBadge status={data.status} label={data.status} size="sm" />
        {onInsight && (
          <button
            className="trend-chart__ai-btn"
            onClick={onInsight}
            title="AI Insights — DLER analysis"
            aria-label="AI Insights for DLER"
          >
            AI
          </button>
        )}
        <CommentButton targetType="metric" targetId="dler" />
      </div>

      <div className="dler">
        <div className="dler__value">
          {noData ? 'N/A' : data.ratio.toFixed(2)}
        </div>
        <div className="dler__benchmark">
          Benchmark: {data.benchmark.toFixed(1)}
        </div>

        <div className="dler__breakdown">
          <div className="dler__row">
            <span>Gross Margin $</span>
            <span>{formatCurrency(data.grossMarginDollars)}</span>
          </div>
          <div className="dler__row">
            <span>Direct Labour $</span>
            <span>{formatCurrency(data.directLabourDollars)}</span>
          </div>
        </div>

        {noData && (
          <p className="scaling-up__warning">
            No direct labour accounts found. Configure account
            keywords to separate wages from operating expenses.
          </p>
        )}
      </div>
    </div>
  );
}
